/**
 * Product Linker Handler
 *
 * Handles search, selection, and rendering behavior for linked products in posts.
 */

import { ProductLinkerApi } from "./product-linker-api";
import { ProductLinkerRender } from "./product-linker-render";
import type { ProductLinkerProduct } from "./product-linker-api";
import type { ProductLinkerForm } from "./product-linker-form";

class ProductLinkerHandler {
    public form: ProductLinkerForm;

    public constructor(form: ProductLinkerForm) {
        this.form = form;
    }

    public shouldSkipRender(): boolean {
        const root = this.form.getRoot();
        return root instanceof HTMLElement && root.dataset.adApplied === "1";
    }

    public handleOpenClick(_event: Event): void {
        this.form.focusSearchInput();
    }

    public handleClearClick(_event: Event): void {
        this.form.setProductId("");
        delete this.form.getRoot().dataset.productLinkerUnresolved;
        this.form.getRoot().classList.remove("has-product");
        ProductLinkerRender.renderEmpty(this.form.getPreviewElement());
    }

    public handleProductSelected(event: CustomEvent<{ product?: ProductLinkerProduct }>): void {
        // The WYSIWYG toolbar's "insert product" button reuses this same search
        // input/modal to embed a product card into the content instead of
        // setting the post's single linked product — see
        // wysiwyg/wysiwyg-product-embed/. Skip when that flow owns this pick.
        if (this.form.getSearchInput()?.dataset.wysiwygEmbedPending === "1") {
            return;
        }

        const selectedProduct = event?.detail?.product;
        if (!selectedProduct) return;

        const selectedProductId = selectedProduct.product_id;
        if (!Number.isInteger(selectedProductId) || selectedProductId <= 0) return;

        this.form.setProductId(selectedProductId);
        delete this.form.getRoot().dataset.productLinkerUnresolved;
        this.form.getRoot().classList.add("has-product");
        ProductLinkerRender.renderProduct(this.form.getPreviewElement(), selectedProduct);
        this.form.clearSearchInput();
    }

    private notifyContentUpdated(): void {
        const card = this.form.getClosestPostCard(this.form.getRoot());
        const root = card instanceof HTMLElement ? card : this.form.getRoot();

        try {
            document.dispatchEvent(
                new CustomEvent("app:content:inserted", {
                    detail: { root },
                }),
            );
        } catch (_error) {
            void _error;
        }
    }

    public async bootstrapInitial(): Promise<void> {
        if (this.shouldSkipRender()) return;

        // SSR: product card already rendered by Blade (ISS:01-112) — skip API fetch
        const root = this.form.getRoot();
        if (root instanceof HTMLElement && root.dataset.ssrRendered === "1") {
            root.classList.add("has-product");
            return;
        }

        const currentProductId = this.form.getProductId();
        // Keep review_data[post_product_id] (if present) in sync with whatever
        // this linker's own hidden input already holds on initial render —
        // covers validation-failure redisplay via old('post_product_id', ...),
        // which only restores the flat field, not the nested one.
        this.form.setProductId(currentProductId ?? "");
        if (!currentProductId) {
            if (this.shouldSkipRender()) return;
            delete this.form.getRoot().dataset.productLinkerUnresolved;
            this.form.getRoot().classList.remove("has-product");
            ProductLinkerRender.renderEmpty(this.form.getPreviewElement());
            return;
        }

        this.form.getRoot().classList.add("has-product");

        try {
            const linkedProduct = await ProductLinkerApi.fetchProduct(currentProductId);
            if (this.shouldSkipRender()) return;
            if (linkedProduct) {
                delete this.form.getRoot().dataset.productLinkerUnresolved;
                ProductLinkerRender.renderProduct(this.form.getPreviewElement(), linkedProduct);
                return;
            }
        } catch (_error) {
            void _error;
        }

        if (this.shouldSkipRender()) return;
        this.form.getRoot().dataset.productLinkerUnresolved = "1";
        ProductLinkerRender.renderProduct(this.form.getPreviewElement(), {
            product_id: currentProductId,
            product_name: `#${currentProductId}`,
        });
        this.notifyContentUpdated();
    }
}

export { ProductLinkerHandler };
